import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  title: { en: 'Audit Logs', hi: 'ऑडिट लॉग' },
  subtitle: { en: 'Activity trail — record of all actions', hi: 'गतिविधि रिकॉर्ड — सभी कार्रवाइयों का ब्यौरा' },
  total_actions: { en: 'Total Actions', hi: 'कुल कार्रवाइयां' },
  today: { en: 'Today', hi: 'आज' },
  active_users: { en: 'Active Users', hi: 'सक्रिय उपयोगकर्ता' },
  login_events: { en: 'Login Events', hi: 'लॉगिन इवेंट्स' },
  f_all: { en: 'All', hi: 'सभी' },
  f_login: { en: 'Login', hi: 'लॉगिन' },
  f_create: { en: 'Create', hi: 'बनाएं' },
  f_update: { en: 'Update', hi: 'अपडेट' },
  f_delete: { en: 'Delete', hi: 'हटाएं' },
  f_payment: { en: 'Payment', hi: 'भुगतान' },
  no_logs: { en: 'No audit log found', hi: 'कोई ऑडिट लॉग नहीं मिला' },
  c_action: { en: 'Action', hi: 'कार्रवाई' },
  c_user: { en: 'User', hi: 'उपयोगकर्ता' },
  c_details: { en: 'Details', hi: 'विवरण' },
  c_ip: { en: 'IP', hi: 'आईपी' },
  c_time: { en: 'Time', hi: 'समय' },
};

export default function AuditLogsPage({ token }) {
  const { language } = useLanguage();
  const tr = (k) => (T[k] ? T[k][language] || T[k].en : k);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: '', page: 1 });

  const headers = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: filter.page, limit: 50 });
      if (filter.action) params.append('action_type', filter.action);
      const [logsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/audit/logs?${params}`, { headers: headers() }),
        fetch(`${API_URL}/api/audit/logs/stats`, { headers: headers() }),
      ]);
      const logsData = await logsRes.json();
      const statsData = await statsRes.json();
      if (logsData.success) setLogs(logsData.logs || []);
      if (statsData.success) setStats(statsData);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [headers, filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const actionIcons = {
    'login': 'fa-right-to-bracket text-green-500', 'logout': 'fa-right-from-bracket text-gray-500',
    'create': 'fa-plus text-blue-500', 'update': 'fa-pen text-amber-500',
    'delete': 'fa-trash text-red-500', 'view': 'fa-eye text-indigo-500',
    'export': 'fa-download text-cyan-500', 'payment': 'fa-credit-card text-emerald-500',
  };

  const statCards = [
    { label: tr('total_actions'), value: stats?.total_actions || 0, icon: 'fa-list', color: 'from-blue-500 to-blue-600' },
    { label: tr('today'), value: stats?.today_actions || 0, icon: 'fa-calendar-day', color: 'from-emerald-500 to-emerald-600' },
    { label: tr('active_users'), value: stats?.active_users || 0, icon: 'fa-users', color: 'from-violet-500 to-violet-600' },
    { label: tr('login_events'), value: stats?.login_count || 0, icon: 'fa-right-to-bracket', color: 'from-amber-500 to-amber-600' },
  ];

  const filters = [
    { id: '', label: tr('f_all') }, { id: 'login', label: tr('f_login') }, { id: 'create', label: tr('f_create') },
    { id: 'update', label: tr('f_update') }, { id: 'delete', label: tr('f_delete') }, { id: 'payment', label: tr('f_payment') },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="audit-logs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <i className="fa-solid fa-clock-rotate-left text-indigo-500"></i> {tr('title')}
          </h1>
          <p className="text-sm text-gray-500">{tr('subtitle')}</p>
        </div>
        <button onClick={loadData} className="p-2 hover:bg-gray-100 rounded-lg transition" data-testid="audit-refresh-btn">
          <i className="fa-solid fa-arrows-rotate text-gray-500"></i>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="audit-stats">
          {statCards.map((s, i) => (
            <div key={i} className={`bg-gradient-to-br ${s.color} rounded-xl p-4 text-white`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs opacity-80">{s.label}</p>
                <i className={`fa-solid ${s.icon} text-sm opacity-60`}></i>
              </div>
              <p className="text-2xl font-bold">{s.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {filters.map(f => (
          <button key={f.id} onClick={() => setFilter(prev => ({ ...prev, action: f.id, page: 1 }))}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${filter.action === f.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            data-testid={`audit-filter-${f.id || 'all'}`}>{f.label}</button>
        ))}
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-16 text-center">
          <i className="fa-solid fa-clock-rotate-left text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-500 font-medium">{tr('no_logs')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm" data-testid="audit-logs-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{tr('c_action')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{tr('c_user')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{tr('c_details')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{tr('c_ip')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{tr('c_time')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.log_id || i} className="border-b last:border-0 hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <i className={`fa-solid ${actionIcons[log.action_type] || 'fa-circle text-gray-400'} text-sm`}></i>
                      <span className="font-medium text-gray-700 capitalize">{log.action_type || '-'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{log.user_name || log.user_id || '-'}</td>
                  <td className="px-4 py-3 text-gray-500 truncate max-w-xs">{log.description || log.details || '-'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">{log.ip_address || '-'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{log.created_at ? new Date(log.created_at).toLocaleString(language === 'hi' ? 'hi-IN' : 'en-IN') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
